import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Search } from 'lucide-react';
import { isValidPolishNip, type CrmAccount, type CrmAccountStatus } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCrmApi, useRegistryApi } from '@/services/api';
import { t } from '@/i18n';

type RegistryField = 'nip' | 'vatId';

/**
 * NIP and VAT UE share one shape: an input with the registry-lookup button
 * beside it (Biała Lista for the NIP, VIES for the VAT UE id). The whole
 * dialog is frozen on ONE lookup at a time — `fetching` names which field
 * owns the in-flight request, so only that button shows the spinner while
 * both stay disabled.
 */
function RegistryLookupField({
  field, label, value, placeholder, fetching, onChange, onLookup,
}: {
  field: RegistryField;
  label: string;
  value: string;
  placeholder?: string;
  fetching: RegistryField | null;
  onChange: (event: { target: { value: string } }) => void;
  onLookup: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={value} onChange={onChange} placeholder={placeholder} />
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={onLookup}
          disabled={fetching !== null}
        >
          {fetching === field ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Search className="size-3.5" />
          )}
          {t('crm.accounts.form.fetchRegistry')}
        </Button>
      </div>
    </div>
  );
}

interface Props {
  nip: string;
  open: boolean;
  /** null = create, otherwise edit. */
  account: CrmAccount | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

interface FormValues {
  name: string;
  legalName: string;
  nip: string;
  vatId: string;
  website: string;
  emailDomains: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  industry: string;
  status: CrmAccountStatus;
  notes: string;
}

const EMPTY_FORM: FormValues = {
  name: '',
  legalName: '',
  nip: '',
  vatId: '',
  website: '',
  emailDomains: '',
  email: '',
  phone: '',
  street: '',
  postalCode: '',
  city: '',
  country: '',
  industry: '',
  status: 'lead',
  notes: '',
};

/** Digits only — the identity of a typed NIP, whatever its spelling. */
const nipDigits = (value: string): string => value.replace(/\D/g, '');

export function AccountFormDialog({ nip, open, account, onOpenChange, onSaved }: Props) {
  const { createAccount, updateAccount } = useCrmApi();
  const { lookupRegistry } = useRegistryApi();
  const [form, setForm] = useState<FormValues>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingRegistry, setFetchingRegistry] = useState<RegistryField | null>(null);
  /**
   * Digits of the last NIP the auto-lookup already fired for in THIS dialog
   * instance — so a user who clears the fetched name is not fought over by the
   * automation. Compared as DIGITS, so `PL 525-244-5767` and `5252445767` are
   * one value and not two lookups.
   */
  const autoFetchedNip = useRef<string | null>(null);
  /**
   * Bumped on every open (and close) of the dialog. A lookup captures it
   * before its await and drops the answer when it moved on — otherwise a slow
   * White List response lands in the form of the NEXT account the user opened.
   */
  const openSeq = useRef(0);
  /**
   * Mirrors `fetchingRegistry` for the synchronous onChange guard: the state
   * read inside that closure is the last render's, so it cannot tell whether a
   * lookup is already running.
   */
  const lookupInFlight = useRef(false);

  useEffect(() => {
    openSeq.current += 1;
    lookupInFlight.current = false;
    setFetchingRegistry(null);
    if (!open) return;
    autoFetchedNip.current = null;
    setForm(
      account
        ? {
            name: account.name,
            legalName: account.legalName ?? '',
            nip: account.nip ?? '',
            vatId: account.vatId ?? '',
            website: account.website ?? '',
            emailDomains: account.emailDomains.join(', '),
            email: account.email ?? '',
            phone: account.phone ?? '',
            street: account.street ?? '',
            postalCode: account.postalCode ?? '',
            city: account.city ?? '',
            country: account.country ?? '',
            industry: account.industry ?? '',
            status: account.status,
            notes: account.notes ?? '',
          }
        : EMPTY_FORM,
    );
  }, [open, account]);

  /**
   * Fill EMPTY fields from the government registry (White List for a Polish
   * NIP, VIES for an EU VAT id). `prev.X || data.X` — a registry answer only
   * ever fills a blank, it never overwrites something the user typed.
   * `legalName` is deliberately NOT filled: the registry returns ONE name, and
   * copying it into both fields would make 'Pełna nazwa' a duplicate of
   * 'Nazwa'.
   *
   * `auto` marks the lookup the NIP field fires while the user is still
   * typing: it stays silent about a registry that does not know the id
   * (404) or refuses it (400) — only a deliberate button click gets an error
   * toast. Success is loud on both paths: fields changing under the cursor
   * needs saying.
   */
  const runLookup = async (field: RegistryField, rawId: string, auto = false) => {
    const id = rawId.trim();
    if (!id) {
      if (!auto) toast.warning(t('registry.refreshNoId'));
      return;
    }
    if (lookupInFlight.current) return;
    const seq = openSeq.current;
    lookupInFlight.current = true;
    setFetchingRegistry(field);
    try {
      const data = await lookupRegistry(id);
      if (seq !== openSeq.current) return;
      setForm((prev) => ({
        ...prev,
        name: prev.name || data.name || '',
        street: prev.street || data.street || '',
        postalCode: prev.postalCode || data.postalCode || '',
        city: prev.city || data.city || '',
        country: prev.country || data.country || '',
      }));
      toast.success(t('registry.lookupSuccess'));
    } catch (error) {
      if (auto || seq !== openSeq.current) return;
      const status = (error as { status?: number })?.status;
      // 400 is a malformed id (the button accepts anything typed), 404 an id
      // the registry simply does not carry — both are "not found" to a human;
      // only a broken/unreachable registry is a failure.
      toast.error(
        status === 404 || status === 400
          ? t('registry.lookupNotFound')
          : t('registry.lookupFailed'),
      );
    } finally {
      if (seq === openSeq.current) {
        lookupInFlight.current = false;
        setFetchingRegistry(null);
      }
    }
  };

  const set = (key: keyof FormValues) => (event: { target: { value: string } }) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    // Auto-lookup fires once per typed NIP value, and only while the name is
    // still empty — never for VAT UE (button only, VIES answers are thinner).
    // The value is stamped only when the lookup actually starts: burning it
    // while another one is in flight would mean this NIP is never fetched.
    if (key === 'nip' && isValidPolishNip(value) && form.name.trim() === '') {
      const digits = nipDigits(value);
      if (autoFetchedNip.current !== digits && !lookupInFlight.current) {
        autoFetchedNip.current = digits;
        void runLookup('nip', value, true);
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const dto = {
        name: form.name.trim(),
        legalName: form.legalName.trim() || null,
        nip: form.nip.trim() || null,
        vatId: form.vatId.trim() || null,
        website: form.website.trim() || null,
        emailDomains: form.emailDomains
          .split(',')
          .map((domain) => domain.trim())
          .filter(Boolean),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        street: form.street.trim() || null,
        postalCode: form.postalCode.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        industry: form.industry.trim() || null,
        status: form.status,
        notes: form.notes.trim() || null,
      };
      if (account) await updateAccount(nip, account.id, dto);
      else await createAccount(nip, dto);
      toast.success(t('crm.accounts.saved'));
      onSaved();
    } catch (error) {
      const status = (error as { status?: number })?.status;
      toast.error(status === 409 ? t('crm.accounts.nipConflict') : t('errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {account ? t('common.edit') : t('crm.accounts.newAccount')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.accounts.form.name')}</Label>
            <Input value={form.name} onChange={set('name')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.legalName')}</Label>
            <Input value={form.legalName} onChange={set('legalName')} />
          </div>
          <RegistryLookupField
            field="nip"
            label={t('crm.accounts.form.nip')}
            value={form.nip}
            fetching={fetchingRegistry}
            onChange={set('nip')}
            onLookup={() => runLookup('nip', form.nip)}
          />
          <RegistryLookupField
            field="vatId"
            label={t('crm.accounts.form.vatId')}
            value={form.vatId}
            placeholder="np. DE123456789"
            fetching={fetchingRegistry}
            onChange={set('vatId')}
            onLookup={() => runLookup('vatId', form.vatId)}
          />
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.website')}</Label>
            <Input value={form.website} onChange={set('website')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.status')}</Label>
            <Select
              value={form.status}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, status: value as CrmAccountStatus }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['lead', 'prospect', 'customer', 'inactive'] as CrmAccountStatus[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`crm.accountStatus.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.accounts.form.emailDomains')}</Label>
            <Input
              value={form.emailDomains}
              onChange={set('emailDomains')}
              placeholder="fss.cc, fss.pl"
            />
            <p className="text-muted-foreground text-xs">
              {t('crm.accounts.form.emailDomainsHint')}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.email')}</Label>
            <Input value={form.email} onChange={set('email')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.phone')}</Label>
            <Input value={form.phone} onChange={set('phone')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.street')}</Label>
            <Input value={form.street} onChange={set('street')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.postalCode')}</Label>
            <Input value={form.postalCode} onChange={set('postalCode')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.city')}</Label>
            <Input value={form.city} onChange={set('city')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.country')}</Label>
            <Input value={form.country} onChange={set('country')} />
          </div>
          <div className="space-y-1.5">
            <Label>{t('crm.accounts.form.industry')}</Label>
            <Input value={form.industry} onChange={set('industry')} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t('crm.accounts.form.notes')}</Label>
            <Textarea value={form.notes} onChange={set('notes')} rows={3} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={form.name.trim() === '' || submitting}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
